<template>
  <div class="sales-summary">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-chart-line"></i> Sales Summary
            </h2>
            <p class="text-white opacity-75">Comprehensive overview of your sales performance and metrics</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter"></i> Filters</h4>
        </div>
        <el-form :inline="true" size="small">
          <el-form-item label="Date Range">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="MMM dd, yyyy"
              value-format="yyyy-MM-dd"
              @change="handleDateChange"
            />
          </el-form-item>
          <el-form-item label="Employee">
            <el-select
              v-model="filters.user_id"
              multiple
              placeholder="All Employees"
              @change="loadData"
            >
              <el-option
                v-for="user in users"
                :key="user.id"
                :label="user.name"
                :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Customer">
            <el-select
              v-model="filters.customer_id"
              multiple
              placeholder="All Customers"
              @change="loadData"
            >
              <el-option
                v-for="customer in customers"
                :key="customer.id"
                :label="customer.name"
                :value="customer.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-download" @click="exportData" :loading="loading">
              Export PDF
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading sales data...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Summary Cards -->
        <el-row :gutter="20" class="m-b-20">
          <el-col :xs="24" :sm="12" :md="6" v-for="metric in summaryMetrics" :key="metric.title" class="m-b-20">
            <el-card :body-style="{ padding: '20px' }" class="metric-card">
              <div class="metric-content">
                <div class="metric-info">
                  <h4 class="metric-title">{{ metric.title }}</h4>
                  <h2 class="metric-value">{{ metric.value }}</h2>
                  <div class="metric-trend">
                    <span :class="['trend', metric.trend > 0 ? 'positive' : 'negative']">
                      <i :class="metric.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(metric.trend) }}%
                    </span>
                    <span class="trend-text">vs last period</span>
                  </div>
                </div>
                <div class="metric-icon">
                  <i :class="metric.icon"></i>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Sales Trends Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Sales Trends</h4>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Payment Methods Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-credit-card-outline"></i> Payment Methods</h4>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="paymentMethodsData.length > 0"
                  :chart-data="paymentMethodsChartData"
                  :chart-options="paymentMethodsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-credit-card-outline" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Performance Tables -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Top Selling Items -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-package-variant"></i> Top Selling Items</h4>
                <el-tag size="mini" type="info">Last 30 days</el-tag>
              </div>
              <div class="product-list">
                <div
                  v-for="(item, index) in topItemsData.slice(0, 5)"
                  :key="item.item_id"
                  class="product-item"
                >
                  <div class="product-rank">{{ index + 1 }}</div>
                  <div class="product-info">
                    <div class="product-name">{{ item.item?.name || 'Unknown Item' }}</div>
                    <div class="product-stats">
                      <span class="units">{{ item.total_quantity }} units</span>
                      <span class="revenue">{{ formatCurrency(item.total_revenue) }}</span>
                    </div>
                    <el-progress
                      :percentage="(item.total_quantity / Math.max(...topItemsData.map(i => i.total_quantity))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="product-trend">
                    <span class="orders">{{ item.total_orders }} orders</span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Employee Performance -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-account-group"></i> Employee Performance</h4>
                <el-tag size="mini" type="info">Last 30 days</el-tag>
              </div>
              <div class="employee-list">
                <div
                  v-for="(employee, index) in employeeData.slice(0, 5)"
                  :key="employee.user_id"
                  class="employee-item"
                >
                  <div class="employee-rank">{{ index + 1 }}</div>
                  <div class="employee-info">
                    <div class="employee-name">{{ employee.user?.name || 'Unknown Employee' }}</div>
                    <div class="employee-stats">
                      <span class="orders">{{ employee.total_orders }} orders</span>
                      <span class="sales">{{ formatCurrency(employee.total_sales) }}</span>
                    </div>
                    <el-progress
                      :percentage="(employee.total_sales / Math.max(...employeeData.map(e => e.total_sales))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="employee-trend">
                    <span class="avg-order">{{ formatCurrency(employee.average_order_value) }} avg</span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Recent Sales Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-receipt"></i> Recent Sales</h4>
            <el-button type="text" @click="loadDetailedSales">
              View All <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
          <el-table :data="recentSales" style="width: 100%" size="small">
            <el-table-column prop="code" label="Order ID" width="120" />
            <el-table-column label="Customer" width="200">
              <template slot-scope="scope">
                {{ scope.row.customer?.name || 'Walk-in Customer' }}
              </template>
            </el-table-column>
            <el-table-column label="Employee" width="150">
              <template slot-scope="scope">
                {{ scope.row.user?.name || 'Unknown' }}
              </template>
            </el-table-column>
            <el-table-column label="Amount" width="120">
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.total_price) }}
              </template>
            </el-table-column>
            <el-table-column label="Status" width="100">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.balance === 0 ? 'success' : 'warning'"
                  size="mini"
                >
                  {{ scope.row.balance === 0 ? 'Paid' : 'Partial' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Date" width="150">
              <template slot-scope="scope">
                {{ formatDate(scope.row.created_at) }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'

export default {
  name: 'SalesSummary',
  components: {
    LineChart,
    DoughnutChart
  },
  data () {
    return {
      loading: false,
      summary: {
        total_sales: 0,
        total_revenue: 0,
        total_orders: 0,
        average_order_value: 0,
        total_paid: 0,
        total_balance: 0,
        total_discount: 0,
        total_profit: 0,
        profit_margin: 0,
        growth_rate: 0
      },
      dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
      filters: {
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        user_id: [],
        customer_id: []
      },
      users: [],
      customers: [],
      trendsData: [],
      paymentMethodsData: [],
      topItemsData: [],
      employeeData: [],
      recentSales: []
    }
  },
  computed: {
    summaryMetrics () {
      const growth = this.summary.growth_rate || 0
      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(this.summary.total_revenue || this.summary.total_sales),
          trend: growth,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Orders',
          value: this.summary.total_orders,
          trend: growth,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Order Value',
          value: this.formatCurrency(this.summary.average_order_value),
          trend: growth,
          icon: 'mdi mdi-chart-line'
        },
        {
          title: 'Profit Margin',
          value: (this.summary.profit_margin || 0) + '%',
          trend: growth,
          icon: 'mdi mdi-chart-areaspline'
        }
      ]
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => this.formatDate(item.date)),
        datasets: [
          {
            label: 'Revenue',
            data: this.trendsData.map(item => item.revenue),
            borderColor: '#409EFF',
            backgroundColor: 'rgba(64, 158, 255, 0.1)',
            tension: 0.4
          },
          {
            label: 'Orders',
            data: this.trendsData.map(item => item.orders),
            borderColor: '#67C23A',
            backgroundColor: 'rgba(103, 194, 58, 0.1)',
            tension: 0.4
          }
        ]
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    },
    paymentMethodsChartData () {
      return {
        labels: this.paymentMethodsData.map(item => item.payment_method_name),
        datasets: [
          {
            data: this.paymentMethodsData.map(item => item.total_amount),
            backgroundColor: [
              '#409EFF',
              '#67C23A',
              '#E6A23C',
              '#F56C6C',
              '#909399',
              '#9C27B0'
            ]
          }
        ]
      }
    },
    paymentMethodsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    }
  },
  mounted () {
    this.loadData()
    this.loadUsers()
    this.loadCustomers()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.loadData()
      }
    },
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadSalesSummary(),
          this.loadSalesTrends(),
          this.loadPaymentMethods(),
          this.loadTopItems(),
          this.loadEmployeeData(),
          this.loadRecentSales()
        ])
      } catch (error) {
        this.handleError(error, 'Failed to load sales data')
      } finally {
        this.loading = false
      }
    },
    async loadSalesSummary () {
      try {
        const response = await this.$reportService.getSalesSummary(this.filters)
        if (response.status === 'success') {
          this.summary = response.data.summary || response.data || {}
        }
      } catch (error) {
        console.error('Error loading sales summary:', error)
      }
    },
    async loadSalesTrends () {
      try {
        const response = await this.$reportService.getSalesTrends(this.filters)
        if (response.status === 'success') {
          this.trendsData = response.data
        }
      } catch (error) {
        console.error('Error loading sales trends:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        const response = await this.$reportService.getSalesByPaymentMethod(this.filters)
        if (response.status === 'success') {
          this.paymentMethodsData = response.data
        }
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async loadTopItems () {
      try {
        const response = await this.$reportService.getTopSellingItems(this.filters)
        if (response.status === 'success') {
          this.topItemsData = response.data
        }
      } catch (error) {
        console.error('Error loading top items:', error)
      }
    },
    async loadEmployeeData () {
      try {
        const response = await this.$reportService.getSalesByEmployee(this.filters)
        if (response.status === 'success') {
          this.employeeData = response.data
        }
      } catch (error) {
        console.error('Error loading employee data:', error)
      }
    },
    async loadRecentSales () {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const response = await this.$reportService.getSalesDetailed({
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          per_page: 10
        })
        if (response.status === 'success') {
          this.recentSales = response.data.data
        }
      } catch (error) {
        console.error('Error loading recent sales:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async exportData () {
      try {
        this.loading = true
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const response = await this.$reportService.getSalesSummary({
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'pdf'
        })
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.loading = false
      }
    },
    loadDetailedSales () {
      this.$router.push('/reporting/query-sales')
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },

    handleError (error, message) {
      console.error(message, error)
      this.$message && this.$message.error(message)
    }
  }
}
</script>

<style scoped>
.sales-summary {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.metric-card {
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.metric-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.metric-info {
  flex: 1;
}

.metric-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.metric-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.metric-trend {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.trend-text {
  font-size: 12px;
  color: #909399;
}

.metric-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.chart-container {
  position: relative;
  height: 250px;
}

.product-list, .employee-list {
  max-height: 300px;
  overflow-y: auto;
}

.product-item, .employee-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.product-item:last-child, .employee-item:last-child {
  border-bottom: none;
}

.product-rank, .employee-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.product-info, .employee-info {
  flex: 1;
  margin-right: 12px;
}

.product-name, .employee-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.product-stats, .employee-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.product-trend, .employee-trend {
  text-align: right;
  font-size: 12px;
  color: #909399;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.text-muted {
  color: #909399;
}

.p-40 {
  padding: 40px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.m-b-30 {
  margin-bottom: 30px;
}

.p-b-30 {
  padding-bottom: 30px;
}

.p-t-30 {
  padding-top: 30px;
}
</style>
